import { Component, Input, OnInit, Output, EventEmitter, OnDestroy, ChangeDetectionStrategy, ChangeDetectorRef, OnChanges, SimpleChanges, ViewChild } from '@angular/core';
import { AbstractControl, FormArray, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged, takeUntil, map } from 'rxjs/operators';
import { TranslateService } from '@ngx-translate/core';
import { HttpHelpService } from 'src/app/Data/service/http-help.service';
import { CashPaymentFormComponent } from '../../shared/cash-payment-form/cash-payment-form.component';
import { PaymentLineFactory } from './payment-line-factory';
import { PaymentMethod, PaymentTotals } from './document-payments.model';
import { CheckPaymentFormComponent } from '../../shared/check-payment-form/check-payment-form.component';
import { CreditCardPaymentFormComponent } from '../../shared/credit-card-payment-form/credit-card-payment-form.component';
import { BankTransferPaymentFormComponent } from '../../shared/bank-transfer-payment-form/bank-transfer-payment-form.component';
import { DeductionPaymentFormComponent } from '../../shared/deduction-payment-form/deduction-payment-form.component';

/**
 * @Component DocumentPaymentsComponent
 * @description Component responsible for managing different payment methods (Cash, Check, Bank Transfer, Credit Card)
 * associated with a document. It handles adding, editing, removing, and calculating totals for payment lines.
 * It interacts with child modal components for specific payment type details.
 */
@Component({
	selector: 'app-document-payments',
	templateUrl: './document-payments.component.html',
	styleUrls: ['./document-payments.component.scss'],
	changeDetection: ChangeDetectionStrategy.OnPush
})
export class DocumentPaymentsComponent implements OnInit, OnChanges, OnDestroy {
	/**
	 * @Input paymentsGroup
	 * @description The main FormGroup containing FormArrays for different payment types.
	 * Expected to have 'linesCash', 'linesChecks', 'linesBankPayment', 'linesCredit' FormArrays.
	 */
	@Input() paymentsGroup!: FormGroup;
	/**
	 * @Input documentType
	 * @description The type of the parent document (e.g., invoice, receipt). Currently unused internally but available.
	 */
	@Input() documentType: number | null = null;
	/**
	 * @Input curentLang
	 * @description The current language code ('en', 'ar', 'he') used for displaying labels.
	 */
	@Input() curentLang: string = 'en';

	/**
	 * @Output totalsChanged
	 * @description Emits the calculated totals for each payment type whenever a payment line is added, removed, or modified.
	 */
	@Output() totalsChanged = new EventEmitter<PaymentTotals>();
	/**
	 * @Output paymentMethodAdded
	 * @description Emits the type of payment method when a new line is added (currently unused).
	 */
	@Output() paymentMethodAdded = new EventEmitter<PaymentMethod | undefined>();
	/**
	 * @Output paymentMethodRemoved
	 * @description Emits the type and index of the payment line that was removed.
	 */
	@Output() paymentMethodRemoved = new EventEmitter<{ type: keyof PaymentTotals | string, index: number }>();
	/**
	 * @Output selectedPaymentMethodChange
	 * @description Emits an array of codes ('CASH', 'CHK', 'BT', 'CC') representing the payment methods currently having lines.
	 */
	@Output() selectedPaymentMethodChange = new EventEmitter<string[]>();

	/** @ViewChild Reference to the CashPaymentFormComponent modal. */
	@ViewChild(CashPaymentFormComponent) private cashPaymentFormComponent: CashPaymentFormComponent | undefined;
	/** @ViewChild Reference to the CashPaymentFormComponent modal. */
	@ViewChild(CashPaymentFormComponent) cashForm!: CashPaymentFormComponent;
	/** @ViewChild Reference to the CheckPaymentFormComponent modal. */
	@ViewChild(CheckPaymentFormComponent) checkForm!: CheckPaymentFormComponent;
	/** @ViewChild Reference to the CreditCardPaymentFormComponent modal. */
	@ViewChild(CreditCardPaymentFormComponent) creditForm!: CreditCardPaymentFormComponent;
	/** @ViewChild Reference to the BankTransferPaymentFormComponent modal. */
	@ViewChild(BankTransferPaymentFormComponent) bankForm!: BankTransferPaymentFormComponent;
	/** @ViewChild Reference to the DeductionPaymentFormComponent modal. */
	@ViewChild(DeductionPaymentFormComponent) deductionForm!: DeductionPaymentFormComponent;

	/** @private Subject used to signal component destruction for unsubscribing observables. */
	private destroy$ = new Subject<void>();
	/** @private Subject used to manage the subscription to paymentsGroup value changes. */
	private valueChangesSubscription$ = new Subject<void>();
	/** @private Stores the last emitted totals to avoid redundant emissions. */
	private lastEmittedTotals: PaymentTotals | null = null;
	/** @private Flag indicating whether the component has been successfully initialized (paymentsGroup received). */
	private isInitialized = false;
	/** Array holding the codes of payment methods currently in use. */
	selectedPaymentMethod: string[] = [];

	/** Definitions of available payment methods. */
	paymentMethods: PaymentMethod[] = [
		{ id: 1, code: 'CASH', name_en: 'Cash', name_ar: 'نقدي', name_he: 'מזומן' },
		{ id: 2, code: 'CHK', name_en: 'Check', name_ar: 'شيك', name_he: 'שיק' },
		{ id: 4, code: 'BT', name_en: 'Bank Transfer', name_ar: 'حوالة بنكية', name_he: 'העברה בנקאית' },
		{ id: 3, code: 'CC', name_en: 'Credit Card', name_ar: 'بطاقة ائتمان', name_he: 'כרטיס אשראי' },
		{ id: 5, code: 'DEDUCTION', name_en: 'Deduction from Source', name_ar: 'خصم من المصدر', name_he: 'ניכוי במקור' },
	];

	/** Local storage for fetched master bank data. */
	masterBankData: any[] = [];
	/** Local storage for fetched bank branch data. */
	allBranchesData: any[] = [];

	/** Cache for bank branches, keyed by bank code. */
	branchCache: { [bankCode: string]: any[] } = {};
	/** Cache for branches specific to check lines (managed by child component now). */
	branchesForCheck: { [index: number]: any[] } = {};

	/** Controls the visibility of the generic payment dialog (obsolete, handled by child components). */
	displayPaymentDialog: boolean = false;
	/** The code of the payment method being added/edited in the generic dialog (obsolete). */
	currentPaymentMethodCode: 'CASH' | 'CHK' | 'CC' | 'BT' | null = null;
	/** Flag indicating if the generic dialog form has been submitted (obsolete). */
	submitted = false;
	/** Flag indicating if the generic dialog is in edit mode (obsolete). */
	isEditing: boolean = false;
	/** Index of the line being edited in the generic dialog (obsolete). */
	editingIndex: number | null = null;
	/** Stores original cash lines when opening the generic dialog (obsolete). */
	private originalCashLinesValue: any[] = [];
	/** Flag to enable debug logging. */
	debug: boolean = false;

	constructor(
		private fb: FormBuilder,
		private translate: TranslateService,
		private httpHelpService: HttpHelpService,
		private cdr: ChangeDetectorRef
	) { }

	/**
	 * @method emitSelectedPaymentMethods
	 * @description Emits the current list of selected payment method codes.
	 */
	emitSelectedPaymentMethods() {
		this.selectedPaymentMethodChange.emit(this.selectedPaymentMethod);
	}

	/**
	 * @method ngOnInit
	 * @description Angular lifecycle hook. Initializes the component, fetches master data (banks, branches),
	 * and sets up the component logic if the paymentsGroup is already available.
	 */
	ngOnInit(): void {
		this.loadMasterData();
		if (!this.paymentsGroup) {
			console.warn('[DEBUG/Payments] ngOnInit: paymentsGroup input is MISSING. Waiting for ngOnChanges.');
		} else {
			this.initializeComponentLogic();
		}
	}

	/**
	 * @method ngOnChanges
	 * @description Angular lifecycle hook. Detects changes to input properties, primarily `paymentsGroup`.
	 * Initializes or re-initializes the component logic when `paymentsGroup` becomes available or changes.
	 * @param changes - Object containing changes to input properties.
	 */
	ngOnChanges(changes: SimpleChanges): void {
		let groupNowAvailable = false;

		if (changes['paymentsGroup']) {
			const change = changes['paymentsGroup'];
			if (!change.currentValue) {
				this.valueChangesSubscription$.next();
				this.isInitialized = false;
			} else {
				if (change.currentValue instanceof FormGroup &&
					change.currentValue.get('linesCash') &&
					change.currentValue.get('linesChecks') &&
					change.currentValue.get('linesBankPayment') &&
					change.currentValue.get('linesCredit'))
				{
					groupNowAvailable = true;
					if (change.previousValue !== change.currentValue) {
						this.valueChangesSubscription$.next();
					}
					this.initializeComponentLogic();
				} else {
					this.isInitialized = false;
					this.valueChangesSubscription$.next();
				}
			}
		 }

		if (this.isInitialized && (changes['masterBank'])) {
			this.preloadAllBranches();
		}

		if (groupNowAvailable) {
			this.cdr.markForCheck();
		} else if (changes['paymentsGroup'] && !changes['paymentsGroup'].currentValue) {
			this.cdr.markForCheck();
		}
	}

	/**
	 * @method updateSelectedPaymentMethods
	 * @description Updates the `selectedPaymentMethod` array based on which payment FormArrays currently have lines.
	 */
	updateSelectedPaymentMethods(): void {
		this.selectedPaymentMethod = [];
		if (this.linesCash && this.linesCash.length > 0) this.selectedPaymentMethod.push('CASH');
		if (this.linesChecks && this.linesChecks.length > 0) this.selectedPaymentMethod.push('CHK');
		if (this.linesBankPayment && this.linesBankPayment.length > 0) this.selectedPaymentMethod.push('BT');
		if (this.linesCredit && this.linesCredit.length > 0) this.selectedPaymentMethod.push('CC');
		if (this.linesDeduction && this.linesDeduction.length > 0) this.selectedPaymentMethod.push('DEDUCTION');
	}

	/**
	 * @private
	 * @method initializeComponentLogic
	 * @description Sets up the core component functionality once `paymentsGroup` is confirmed to be available and valid.
	 * This includes preloading branches, subscribing to value changes for total calculation, and marking the component as initialized.
	 */
	private initializeComponentLogic(): void {
		if (this.isInitialized || !this.paymentsGroup) {
			return;
		}

		const linesCash = this.paymentsGroup.get('linesCash') as FormArray;
		const linesChecks = this.paymentsGroup.get('linesChecks') as FormArray;
		const linesBankPayment = this.paymentsGroup.get('linesBankPayment') as FormArray;
		const linesCredit = this.paymentsGroup.get('linesCredit') as FormArray;

		if (!linesCash || !linesChecks || !linesBankPayment || !linesCredit) {
			return;
		}

		this.preloadAllBranches();
		this.valueChangesSubscription$.next();

		this.paymentsGroup.valueChanges.pipe(
			debounceTime(300),
			map(() => this.calculateAllTotals()),
			distinctUntilChanged(),
			takeUntil(this.valueChangesSubscription$),
			takeUntil(this.destroy$)
		).subscribe(totals => {
			if (JSON.stringify(totals) !== JSON.stringify(this.lastEmittedTotals)) {
				this.lastEmittedTotals = { ...totals };
				this.totalsChanged.emit(totals);
				this.cdr.markForCheck();
			}
		});

		this.isInitialized = true;

		setTimeout(() => {
			const initialTotals = this.calculateAllTotals();
			if (JSON.stringify(initialTotals) !== JSON.stringify(this.lastEmittedTotals)) {
				this.lastEmittedTotals = { ...initialTotals };
				this.totalsChanged.emit(initialTotals);
				this.cdr.markForCheck();
			}
		}, 0);
	}

	/**
	 * @method ngOnDestroy
	 * @description Angular lifecycle hook. Cleans up subscriptions by completing the Subjects.
	 */
	ngOnDestroy(): void {
		this.valueChangesSubscription$.next();
		this.valueChangesSubscription$.complete();
		this.destroy$.next();
		this.destroy$.complete();
	}

	/**
	 * @getter linesCash
	 * @description Safely retrieves the 'linesCash' FormArray from the `paymentsGroup`.
	 * @returns {FormArray | null} The FormArray or null if not available.
	 */
	get linesCash(): FormArray | null {
		return this.paymentsGroup?.get('linesCash') as FormArray | null;
	}
	/**
	 * @getter linesChecks
	 * @description Safely retrieves the 'linesChecks' FormArray from the `paymentsGroup`.
	 * @returns {FormArray | null} The FormArray or null if not available.
	 */
	get linesChecks(): FormArray | null {
		return this.paymentsGroup?.get('linesChecks') as FormArray | null;
	}
	/**
	 * @getter linesBankPayment
	 * @description Safely retrieves the 'linesBankPayment' FormArray from the `paymentsGroup`.
	 * @returns {FormArray | null} The FormArray or null if not available.
	 */
	get linesBankPayment(): FormArray | null {
		return this.paymentsGroup?.get('linesBankPayment') as FormArray | null;
	}
	/**
	 * @getter linesCredit
	 * @description Safely retrieves the 'linesCredit' FormArray from the `paymentsGroup`.
	 * @returns {FormArray | null} The FormArray or null if not available.
	 */
	get linesCredit(): FormArray | null {
		return this.paymentsGroup?.get('linesCredit') as FormArray | null;
	}

	/**
	 * @getter linesDeduction
	 * @description Safely retrieves the 'linesDeduction' FormArray from the `paymentsGroup`.
	 * @returns {FormArray | null} The FormArray or null if not available.
	 */
	get linesDeduction(): FormArray | null {
		return this.paymentsGroup?.get('linesDeduction') as FormArray | null;
	}

	/**
	 * @getter allPaymentLinesControls
	 * @description Combines controls from all payment FormArrays into a single array, adding a `paymentTypeCode` property to each.
	 * Used for rendering the combined payment lines table.
	 * @returns {AbstractControl[]} An array of all payment line FormGroups.
	 */
	get allPaymentLinesControls(): AbstractControl[] {
		const combined: AbstractControl[] = [];
		this.linesCash?.controls.forEach((control) => {
			(control as any).paymentTypeCode = 'CASH';
			combined.push(control);
		});
		this.linesChecks?.controls.forEach((control) => {
			(control as any).paymentTypeCode = 'CHK';
			combined.push(control);
		});
		this.linesBankPayment?.controls.forEach((control) => {
			(control as any).paymentTypeCode = 'BT';
			combined.push(control);
		});
		this.linesCredit?.controls.forEach((control) => {
			(control as any).paymentTypeCode = 'CC';
			combined.push(control);
		});
		this.linesDeduction?.controls.forEach((control) => {
			(control as any).paymentTypeCode = 'DEDUCTION';
			combined.push(control);
		});

		combined.sort((a, b) => {
			const typeA = a.get('payment_type')?.value || 0;
			const typeB = b.get('payment_type')?.value || 0;
			return typeA - typeB;
		});

		return combined;
	}

	/**
	 * @method removeLine
	 * @description Removes a payment line from the appropriate FormArray based on its type code and index.
	 * @param paymentTypeCode - The code ('CASH', 'CHK', 'BT', 'CC', 'DEDUCTION') identifying the payment type.
	 * @param index - The index of the line to remove within its FormArray.
	 */
	removeLine(paymentTypeCode: string, index: number): void {
		switch (paymentTypeCode) {
			case 'CASH': if (this.linesCash) this.removeLineFromCash(index); break;
			case 'CHK': if (this.linesChecks) this.removeLineFromChecks(index); break;
			case 'BT': if (this.linesBankPayment) this.removeLineFromBankPayment(index); break;
			case 'CC': if (this.linesCredit) this.removeLineFromCredit(index); break;
			case 'DEDUCTION': if (this.linesDeduction) this.removeLineFromDeduction(index); break;
		}
	}

	/**
	 * @method removeLineFromCash
	 * @description Removes a line from the 'linesCash' FormArray and emits an event.
	 * @param index - The index of the cash line to remove.
	 */
	removeLineFromCash(index: number): void {
		this.linesCash?.removeAt(index);
		this.paymentMethodRemoved.emit({ type: 'cash', index });
		this.cdr.markForCheck();
	}

	/**
	 * @method removeLineFromChecks
	 * @description Removes a line from the 'linesChecks' FormArray and emits an event.
	 * @param index - The index of the check line to remove.
	 */
	removeLineFromChecks(index: number): void {
		this.linesChecks?.removeAt(index);
		delete this.branchesForCheck[index];
		this.paymentMethodRemoved.emit({ type: 'checks', index });
		this.cdr.markForCheck();
	}

	/**
	 * @method removeLineFromBankPayment
	 * @description Removes a line from the 'linesBankPayment' FormArray and emits an event.
	 * @param index - The index of the bank transfer line to remove.
	 */
	removeLineFromBankPayment(index: number): void {
		this.linesBankPayment?.removeAt(index);
		this.paymentMethodRemoved.emit({ type: 'bank', index });
		this.cdr.markForCheck();
	}

	/**
	 * @method removeLineFromCredit
	 * @description Removes a line from the 'linesCredit' FormArray and emits an event.
	 * @param index - The index of the credit card line to remove.
	 */
	removeLineFromCredit(index: number): void {
		this.linesCredit?.removeAt(index);
		this.paymentMethodRemoved.emit({ type: 'credit', index });
		this.cdr.markForCheck();
	}

	/**
	 * @method removeLineFromDeduction
	 * @description Removes a line from the 'linesDeduction' FormArray and emits an event.
	 * @param index - The index of the deduction line to remove.
	 */
	removeLineFromDeduction(index: number): void {
		this.linesDeduction?.removeAt(index);
		this.paymentMethodRemoved.emit({ type: 'deduction', index });
		this.cdr.markForCheck();
	}

	/**
	 * @private
	 * @method createCashLine
	 * @description Creates a FormGroup for a cash payment line using the PaymentLineFactory.
	 * @param data - Optional initial data for the line.
	 * @returns {FormGroup} The created FormGroup.
	 */
	private createCashLine(data?: any): FormGroup {
		return PaymentLineFactory.createCashLine(this.fb, data);
	}
	/**
	 * @private
	 * @method createCheckLine
	 * @description Creates a FormGroup for a check payment line.
	 * @param data - Optional initial data for the line.
	 * @returns {FormGroup} The created FormGroup.
	 */
	private createCheckLine(data?: any): FormGroup {
		return this.fb.group({
			id: data?.id ?? null,
			payment_type: 2,
			date: [data?.date ?? null, Validators.required],
			amount: [data?.amount ?? '', Validators.required],
			bank: [data?.bank ?? ''],
			branch: [data?.branch ?? ''],
			account: [data?.account ?? ''],
			check: [data?.check ?? ''],
		});
	}
	/**
	 * @private
	 * @method createBankPaymentLine
	 * @description Creates a FormGroup for a bank transfer payment line using the PaymentLineFactory.
	 * @param data - Optional initial data for the line.
	 * @returns {FormGroup} The created FormGroup.
	 */
	private createBankPaymentLine(data?: any): FormGroup {
		return PaymentLineFactory.createBankPaymentLine(this.fb, data);
	}
	/**
	 * @private
	 * @method createCreditLine
	 * @description Creates a FormGroup for a credit card payment line using the PaymentLineFactory.
	 * @param data - Optional initial data for the line.
	 * @returns {FormGroup} The created FormGroup.
	 */
	private createCreditLine(data?: any): FormGroup {
		return PaymentLineFactory.createCreditLine(this.fb, data);
	}

	/**
	 * @private
	 * @method createDeductionLine
	 * @description Creates a FormGroup for a deduction from source payment line (like cash, but without 'pate').
	 * @param data - Optional initial data for the line.
	 * @returns {FormGroup} The created FormGroup.
	 */
	private createDeductionLine(data?: any): FormGroup {
		return this.fb.group({
			id: data?.id ?? null,
			payment_type: 5,
			amount: [data?.amount ?? '', Validators.required],
			// Add other fields if needed, but no 'pate'
		});
	}

	/**
	 * @private
	 * @method getLinesArrayByType
	 * @description Retrieves the correct payment FormArray based on the provided type code.
	 * @param type - The payment type code ('CASH', 'CHK', 'CC', 'BT', 'DEDUCTION').
	 * @returns {FormArray | null} The corresponding FormArray or null if the type is invalid or the array doesn't exist.
	 */
	private getLinesArrayByType(type: 'CASH' | 'CHK' | 'CC' | 'BT' | 'DEDUCTION' | null): FormArray | null {
		if (!type) return null;
		switch (type) {
			case 'CASH': return this.linesCash;
			case 'CHK': return this.linesChecks;
			case 'BT': return this.linesBankPayment;
			case 'CC': return this.linesCredit;
			case 'DEDUCTION': return this.linesDeduction;
			default: return null;
		}
	}

	/**
	 * @method openAddPaymentDialog
	 * @description Opens the appropriate child modal component to add a new payment line.
	 * @param code - The code ('CASH', 'CHK', 'CC', 'BT', 'DEDUCTION') of the payment type to add.
	 */
	openAddPaymentDialog(code: 'CASH' | 'CHK' | 'CC' | 'BT' | 'DEDUCTION'): void {
switch (code) {
			case 'CASH': this.cashForm.openDialog('add'); break;
			case 'CHK': this.checkForm.openDialog('add'); break;
			case 'CC': this.creditForm.openDialog('add'); break;
			case 'BT': this.bankForm.openDialog('add'); break;
			case 'DEDUCTION':
				if (!this.deductionForm) {
					console.error('[DocumentPaymentsComponent] deductionForm ViewChild is not available!');
					return;
				}
				// --- FIX: Ensure linesDeduction FormArray exists on the paymentsGroup ---
				if (!this.linesDeduction) {
					console.warn('[DocumentPaymentsComponent] linesDeduction FormArray is missing. Creating it now.');
					this.paymentsGroup.addControl('linesDeduction', this.fb.array([]));
					this.cdr.detectChanges();
				}
				// After ensuring, call again to get the reference
				const linesDeductionArr = this.linesDeduction;
				if (!linesDeductionArr) {
					console.error('[DocumentPaymentsComponent] linesDeduction FormArray is STILL not available after creation!');
					return;
				}
				this.deductionForm.openDialog('add');
				break;
		}
	}

	/**
	 * @method openEditPaymentDialog
	 * @description Opens the appropriate child modal component to edit an existing payment line.
	 * @param code - The code ('CASH', 'CHK', 'CC', 'BT', 'DEDUCTION') of the payment type to edit.
	 * @param index - The index of the line to edit within its FormArray.
	 */
	openEditPaymentDialog(code: 'CASH' | 'CHK' | 'CC' | 'BT' | 'DEDUCTION', index: number): void {
		const arr = this.getLinesArrayByType(code);
		let line = arr?.at(index)?.value;
		if (!line && this.paymentsGroup) {
			const raw = this.paymentsGroup.getRawValue();
			const key = code === 'CASH' ? 'linesCash'
				: code === 'CHK' ? 'linesChecks'
				: code === 'BT' ? 'linesBankPayment'
				: code === 'CC' ? 'linesCredit'
				: code === 'DEDUCTION' ? 'linesDeduction'
				: '';
			if (raw && raw[key] && Array.isArray(raw[key]) && raw[key][index]) {
				line = raw[key][index];
			}
		}
		this.cdr.detectChanges();
		switch (code) {
			case 'CASH': this.cashForm.openDialog('edit', line, index); break;
			case 'CHK': this.checkForm.openDialog('edit', line, index); break;
			case 'CC': this.creditForm.openDialog('edit', line, index); break;
			case 'BT':
				this.bankForm.masterBank = this.masterBankData;
				this.bankForm.openDialog('edit', line, index);
				break;
			case 'DEDUCTION':
				this.deductionForm.openDialog('edit', line, index);
				break;
		}
	}

	/**
	 * @method calculateTotalFor
	 * @description Calculates the sum of 'amount' values for all controls within a given FormArray.
	 * @param formArray - The FormArray to calculate the total for.
	 * @returns {number} The calculated total amount.
	 */
	calculateTotalFor(formArray: FormArray | null): number {
		if (!formArray) return 0;
		return formArray.controls.reduce((sum, line) => {
			const amount = line.get('amount')?.value || 0;
			return sum + (Number(amount) || 0);
		}, 0);
	}

	/**
	 * @method calculateAllTotals
	 * @description Calculates the totals for all payment types.
	 * @returns {PaymentTotals & { deduction?: number }} An object containing the total amount for each payment type (cash, checks, bank, credit, deduction).
	 */
	calculateAllTotals(): PaymentTotals & { deduction?: number } {
		return {
			cash: this.calculateTotalFor(this.linesCash),
			checks: this.calculateTotalFor(this.linesChecks),
			bank: this.calculateTotalFor(this.linesBankPayment),
			credit: this.calculateTotalFor(this.linesCredit),
			deduction: this.calculateTotalFor(this.linesDeduction),
		};
	}

	/**
	 * @method preloadAllBranches
	 * @description Populates the `branchCache` by organizing the fetched `allBranchesData` by bank code.
	 * Avoids redundant processing if the cache is already populated.
	 * @returns {Promise<void>}
	 */
	async preloadAllBranches(): Promise<void> {
		if (this.allBranchesData && this.allBranchesData.length > 0 && Object.keys(this.branchCache).length > 0) {
			return;
		}
		try {
			if (!this.allBranchesData || this.allBranchesData.length === 0) {
				this.allBranchesData = [];
				this.branchCache = {};
				return;
			}

			this.branchCache = this.allBranchesData.reduce((cache, branch) => {
				const bankCode = String(branch.bank_code);
				if (!cache[bankCode]) {
					cache[bankCode] = [];
				}
				cache[bankCode].push(branch);
				return cache;
			}, {});
			this.cdr.markForCheck();
		} catch (error) {
			console.error('[DEBUG/Payments] Error building branch cache:', error);
			this.branchCache = {};
		}
	}

	/**
	 * @method loadMasterData
	 * @description Fetches active banks and all bank branches from the backend using `HttpHelpService`.
	 * Populates `masterBankData` and `allBranchesData`, then triggers branch preloading.
	 */
	loadMasterData(): void {
		this.httpHelpService.getActiveBanks().subscribe({
			next: (banks) => {
				this.masterBankData = banks || [];
				this.cdr.markForCheck();
			},
			error: (err) => {
				console.error('[DEBUG/Payments] Error fetching active banks:', err);
				this.masterBankData = [];
				this.cdr.markForCheck();
			}
		});
		this.httpHelpService.getAllBankBranch().subscribe({
			next: (branches) => {
				this.allBranchesData = branches || [];
				this.preloadAllBranches();
				this.cdr.markForCheck();
			},
			error: (err) => {
				console.error('[DEBUG/Payments] Error fetching all branches:', err);
				this.allBranchesData = [];
				this.branchCache = {};
				this.cdr.markForCheck();
			}
		});
	}

	/**
	 * @method getBranchesForBank
	 * @description Retrieves the cached list of branches for a specific bank code.
	 * @param bankCode - The code of the bank.
	 * @returns {any[]} An array of branch objects for the given bank, or an empty array if not found or invalid code.
	 */
	getBranchesForBank(bankCode: string | number | null): any[] {
		if (bankCode === null || bankCode === undefined) return [];
		const codeStr = String(bankCode);
		return this.branchCache[codeStr] || [];
	}

	/**
	 * @method getBankLabel
	 * @description Returns the appropriate bank name based on the current language.
	 * @param item - The bank data object (expected to have bank_name_en, bank_name_ar, bank_name_he).
	 * @returns {string} The localized bank name.
	 */
	getBankLabel(item: any): string {
		if (!item) return '';
		return this.curentLang === 'ar' ? item.bank_name_ar :
			this.curentLang === 'he' ? item.bank_name_he :
				item.bank_name_en;
	}

	/**
	 * @method getBranchLabel
	 * @description Returns the appropriate branch name based on the current language.
	 * @param item - The branch data object (expected to have branch_name_en, branch_name_ar, branch_name_he).
	 * @returns {string} The localized branch name.
	 */
	getBranchLabel(item: any): string {
		if (!item) return '';
		return this.curentLang === 'ar' ? item.branch_name_ar :
			this.curentLang === 'he' ? item.branch_name_he :
				item.branch_name_en;
	}

	/**
	 * @method populatePayments
	 * @description Clears existing payment lines and populates the FormArrays with new data,
	 * typically received when loading an existing document. Prioritizes `receiptsDetails` if available.
	 * Ensures dates are validated and formatted correctly.
	 * @param data - An object containing arrays of payment line data (`linesCash`, `linesChecks`, etc.) or `receiptsDetails`.
	 */
	populatePayments(data: { linesCash?: any[], linesChecks?: any[], linesBankPayment?: any[], linesCredit?: any[], linesDeduction?: any[], receiptsDetails?: any[] }): void {
		if (!this.isInitialized || !this.paymentsGroup) {
			return;
		}

		if (!this.linesCash || !this.linesChecks || !this.linesBankPayment || !this.linesCredit || !this.linesDeduction) {
			return;
		}

		this.clearAllLines();

		const formatDate = (date: string | null): string | null => {
			if (!date) return null;
			const parsedDate = new Date(date);
			if (isNaN(parsedDate.getTime())) {
				console.warn(`[DocumentPaymentsComponent] Invalid date encountered: ${date}`);
				return null;
			}
			const formattedDate = parsedDate.toISOString().split('T')[0]; // Format as 'yyyy-MM-dd'
			return formattedDate;
		};

		if (Array.isArray(data.receiptsDetails) && data.receiptsDetails.length > 0) {
			const sorted = [...data.receiptsDetails].sort((a, b) => {
				const aId = a.line_id ?? a.id ?? 0;
				const bId = b.line_id ?? b.id ?? 0;
				return aId - bId;
			});
			sorted.forEach((item) => {
				const type = Number(item.payment_type);
				const formattedDate = formatDate(item.date);
				if (type === 1) {
					this.linesCash?.push(this.createCashLine({
						...item,
						date: formattedDate,
						amount: item.amount ?? '',
					}));
				} else if (type === 2) {
					this.linesChecks?.push(this.createCheckLine({
						...item,
						date: formattedDate,
						bank: item.bank ?? '',
						branch: item.branch ?? '',
						account_number: item.account ?? item.account_number ?? '',
						check: item.check ?? '',
						amount: item.amount ?? '',
					}));
				} else if (type === 3) {
					this.linesCredit?.push(this.createCreditLine({
						...item,
						date: formattedDate,
						account: item.account ?? item.account_number ?? '',
						amount: item.amount ?? '',
					}));
				} else if (type === 4) {
					this.linesBankPayment?.push(this.createBankPaymentLine({
						...item,
						date: formattedDate,
						bank: item.bank ?? '',
						branch: item.branch ?? '',
						account_number: item.account ?? item.account_number ?? '',
						amount: item.amount ?? '',
					}));
				} else if (type === 5) {
					this.linesDeduction?.push(this.createDeductionLine({
						...item,
						date: formattedDate,
						amount: item.amount ?? '',
					}));
				}
			});
		} else {
			data.linesCash?.forEach((item) => {
				const formattedDate = formatDate(item.date);
				this.linesCash?.push(this.createCashLine({ ...item, date: formattedDate }));
			});
			data.linesChecks?.forEach((item) => {
				const formattedDate = formatDate(item.date);
				this.linesChecks?.push(this.createCheckLine({ ...item, date: formattedDate }));
			});
			data.linesBankPayment?.forEach((item) => {
				const formattedDate = formatDate(item.date);
				this.linesBankPayment?.push(this.createBankPaymentLine({ ...item, date: formattedDate }));
			});
			data.linesCredit?.forEach((item) => {
				const formattedDate = formatDate(item.date);
				this.linesCredit?.push(this.createCreditLine({ ...item, date: formattedDate }));
			});
			data.linesDeduction?.forEach((item) => {
				const formattedDate = formatDate(item.date);
				this.linesDeduction?.push(this.createDeductionLine({ ...item, date: formattedDate }));
			});
		}

		this.cdr.detectChanges();
		this.cdr.markForCheck();
		this.updateSelectedPaymentMethods();

		setTimeout(() => {
			const currentTotals = this.calculateAllTotals();
			if (JSON.stringify(currentTotals) !== JSON.stringify(this.lastEmittedTotals)) {
				this.lastEmittedTotals = { ...currentTotals };
				this.totalsChanged.emit(currentTotals);
				this.cdr.markForCheck();
			}
		}, 0);
	}

	/**
	 * @method clearAllLines
	 * @description Clears all controls from all payment FormArrays.
	 */
	clearAllLines(): void {
		this.linesCash?.clear();
		this.linesChecks?.clear();
		this.linesBankPayment?.clear();
		this.linesCredit?.clear();
		this.linesDeduction?.clear();
	}

	/**
	 * @method onCashLinesSaved
	 * @description Callback executed when the cash payment modal saves changes. Updates the 'linesCash' FormArray.
	 * @param lines - The array of saved cash payment lines.
	 */
	onCashLinesSaved(lines: any[]) {
		this.linesCash?.clear();
		lines.forEach(line => this.linesCash?.push(this.createCashLine(line)));
		this.cdr.markForCheck();
		this.updateSelectedPaymentMethods();
	}

	/**
	 * @method onCheckLinesSaved
	 * @description Callback executed when the check payment modal saves changes. Updates the 'linesChecks' FormArray.
	 * @param lines - The array of saved check payment lines.
	 */
	onCheckLinesSaved(lines: any[]) {
		this.linesChecks?.clear();
		lines.forEach(line => this.linesChecks?.push(this.createCheckLine(line)));
		this.cdr.markForCheck();
		this.updateSelectedPaymentMethods();
	}

	/**
	 * @method onCreditLinesSaved
	 * @description Callback executed when the credit card payment modal saves changes. Updates the 'linesCredit' FormArray.
	 * @param lines - The array of saved credit card payment lines.
	 */
	onCreditLinesSaved(lines: any[]) {
		this.linesCredit?.clear();
		lines.forEach((line) => {
			const group = this.createCreditLine({
				...line,
				account_number: line.account ?? line.account_number ?? '',
				account: line.account ?? line.account_number ?? '',
			});
			this.linesCredit?.push(group);
		});
		this.cdr.markForCheck();
		this.updateSelectedPaymentMethods();
	}

	/**
	 * @method onBankTransferLinesSaved
	 * @description Callback executed when the bank transfer payment modal saves changes. Updates the 'linesBankPayment' FormArray.
	 * @param lines - The array of saved bank transfer payment lines.
	 */
	onBankTransferLinesSaved(lines: any[]) {
		this.linesBankPayment?.clear();
		lines.forEach(line => this.linesBankPayment?.push(this.createBankPaymentLine(line)));
		this.cdr.markForCheck();
		this.updateSelectedPaymentMethods();
	}

	/**
	 * @method onDeductionLinesSaved
	 * @description Callback executed when the deduction payment modal saves changes. Updates the 'linesDeduction' FormArray.
	 * @param lines - The array of saved deduction payment lines.
	 */
	onDeductionLinesSaved(lines: any[]) {
		this.linesDeduction?.clear();
		lines.forEach(line => this.linesDeduction?.push(this.createDeductionLine(line)));
		this.cdr.markForCheck();
		this.updateSelectedPaymentMethods();
	}

}
